import {
  CONTENT_STORAGE_KEYS,
  DEFAULT_SETTINGS,
  POPUP_STORAGE_KEYS,
  normalizePracticeMode,
  syncPracticeFlags,
  type Settings,
  type StorageKey,
} from './schema.ts';
import { mergeShortcutBindings } from '../shortcuts/bindings.ts';
import { normalizeCustomStylePresets, readLegacyPresetLanguages } from '../styles/customStylePresets.ts';
import { normalizeStyleLanguagePriorities } from '../styles/styleLanguagePriorities.ts';

export type Migration = {
  targetVersion: string;
  patch: Partial<Settings>;
};

/** Ordered migrations applied when stored version lags schema (mirrors background.js). */
export const MIGRATIONS: Migration[] = [
  {
    targetVersion: '10',
    patch: {
      version: '10',
      mainStrokeWidth: 0,
      mainStrokeColor: '#000000',
      secondStrokeWidth: 0,
      secondStrokeColor: '#000000',
    },
  },
  {
    targetVersion: '11',
    patch: {
      version: '11',
      shortcutBindings: { ...DEFAULT_SETTINGS.shortcutBindings },
    },
  },
  {
    targetVersion: '12',
    patch: {
      version: '12',
      mainLetterSpacing: 0,
      mainLineHeight: 1.35,
      mainPadding: 8,
      mainOpacity: 1,
      mainBoxShadow: '',
      secondLetterSpacing: 0,
      secondLineHeight: 1.35,
      secondPadding: 8,
      secondOpacity: 1,
      secondBoxShadow: '',
    },
  },
  {
    targetVersion: '13',
    patch: {
      version: '13',
      // practiceMode + flags normalized in applyMigrations (missing → watch).
    },
  },
  {
    targetVersion: '14',
    patch: {
      version: '14',
      // Furigana convert mode is locked; drop legacy normal/spaced/okurigana.
      furiganaMode: 'furigana',
    },
  },
  {
    targetVersion: '15',
    patch: {
      version: '15',
      customStylePresets: [],
    },
  },
  {
    targetVersion: '16',
    patch: {
      version: '16',
      // customStylePresets[].languages was briefly used; lifted in v17.
    },
  },
  {
    targetVersion: '17',
    patch: {
      version: '17',
      // Do NOT reset styleLanguagePriorities here — Studio may have written
      // bindings before the SW migration runs. Empty default is applied by
      // normalizeStyleLanguagePriorities when the key is missing.
    },
  },
  {
    targetVersion: '18',
    patch: {
      version: '18',
      // practiceMode `dubbing` → `shadowing` in normalizePracticeMode / applyMigrations.
      // shadowingHideUpcoming filled when missing after the loop (do not clobber).
    },
  },
  {
    targetVersion: '19',
    patch: {
      version: '19',
      // shadowingGrayPast filled when missing after the loop (do not clobber a
      // user value written before onInstalled migration runs).
    },
  },
  {
    targetVersion: '20',
    patch: {
      version: '20',
      // shadowingAutoRemoveLineBreaks filled when missing after the loop.
    },
  },
  {
    targetVersion: '21',
    patch: {
      version: '21',
      // subtitleOptimize filled when missing after the loop (default on).
    },
  },
  {
    targetVersion: '22',
    patch: {
      version: '22',
      // subtitleOptimizeModel filled when missing after the loop (default small at the time).
    },
  },
  {
    targetVersion: '23',
    patch: {
      version: '23',
      // subtitleOptimizeModel: prefer auto when missing/invalid (member→Large, else Small).
    },
  },
  {
    targetVersion: '24',
    patch: {
      version: '24',
      // preProcessing1/3 defaults live in DEFAULT_SETTINGS / onInstalled / reset only.
      // Do not fill missing keys here — legacy users with undefined stay off via Boolean().
    },
  },
];

export function getInitialInstallDefaults(): Partial<Settings> {
  const {
    version: _version,
    userId: _userId,
    userToken: _userToken,
    statusJson: _statusJson,
    ...installDefaults
  } = DEFAULT_SETTINGS;
  return installDefaults;
}

export function applyMigrations(
  stored: Partial<Settings> & Record<string, unknown>,
): Partial<Settings> {
  let result = { ...stored };
  let currentVersion = typeof stored.version === 'string' ? stored.version : undefined;

  for (const migration of MIGRATIONS) {
    const currentNum = currentVersion === undefined ? -1 : Number(currentVersion);
    const targetNum = Number(migration.targetVersion);
    if (Number.isNaN(targetNum) || currentNum >= targetNum) {
      continue;
    }
    result = { ...result, ...migration.patch };
    currentVersion = migration.targetVersion;
  }

  // Always normalize bindings so partial/corrupt sync blobs stay complete.
  result.shortcutBindings = mergeShortcutBindings(
    (result.shortcutBindings ?? stored.shortcutBindings) as Settings['shortcutBindings'] | undefined,
  );

  // Missing / invalid practiceMode → watch; align legacy boolean flags.
  Object.assign(result, syncPracticeFlags(normalizePracticeMode(result.practiceMode)));

  // Shadowing toggles: default only when absent so a pre-migration popup write
  // (e.g. user turned gray-past off) is not overwritten by version bumps.
  if (result.shadowingHideUpcoming === undefined) {
    result.shadowingHideUpcoming = false;
  }
  if (result.shadowingGrayPast === undefined) {
    result.shadowingGrayPast = true;
  }
  if (result.shadowingAutoRemoveLineBreaks === undefined) {
    result.shadowingAutoRemoveLineBreaks = true;
  }
  if (result.subtitleOptimize === undefined) {
    result.subtitleOptimize = true;
  }
  if (
    result.subtitleOptimizeModel !== 'auto'
    && result.subtitleOptimizeModel !== 'small'
    && result.subtitleOptimizeModel !== 'large'
  ) {
    result.subtitleOptimizeModel = 'auto';
  }

  const legacyLang = readLegacyPresetLanguages(result.customStylePresets);
  result.customStylePresets = normalizeCustomStylePresets(result.customStylePresets);
  result.styleLanguagePriorities = normalizeStyleLanguagePriorities(
    result.styleLanguagePriorities,
    result.customStylePresets,
    legacyLang,
  );

  return result;
}

export function keysForSyncSurface(surface: 'content' | 'popup'): StorageKey[] {
  return surface === 'content'
    ? [...CONTENT_STORAGE_KEYS]
    : [...POPUP_STORAGE_KEYS];
}
